<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Quote Requests</h1>
            <p class="text-gray-600">Super Admin Dashboard</p>
          </div>
          <button @click="navigateTo('/admin/dashboard')" class="btn-tertiary">
            Back to Dashboard
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error loading data</h3>
            <p class="mt-1 text-sm text-red-700">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div v-else class="space-y-8">
        <!-- Pending Quote Requests -->
        <div class="space-y-6">
          <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-lg font-medium text-gray-900">Pending Quote Requests</h2>
            <p class="mt-1 text-sm text-gray-600">{{ quoteRequests.length }} pending requests</p>
          </div>

        <!-- Quote Request Cards -->
        <div v-if="quoteRequests.length > 0" class="space-y-4">
          <div
            v-for="request in quoteRequests"
            :key="request.id"
            class="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 p-6"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <div class="flex items-center justify-between">
                  <h3 class="text-lg font-medium text-gray-900">{{ request.name }}</h3>
                  <span class="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-medium bg-amber-50 text-amber-700">Quote Requested</span>
                </div>
                
                <div class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p class="text-sm text-gray-600">
                      <span class="font-medium">Client:</span> {{ request.client_name }}
                    </p>
                    <p class="text-sm text-gray-600">
                      <span class="font-medium">Email:</span> {{ request.client_email }}
                    </p>
                    <p class="text-sm text-gray-600">
                      <span class="font-medium">Tablets:</span> {{ request.tablet_count }}
                    </p>
                  </div>
                  <div>
                    <p class="text-sm text-gray-600">
                      <span class="font-medium">Start Date:</span> {{ formatDate(request.start_date) }}
                    </p>
                    <p class="text-sm text-gray-600">
                      <span class="font-medium">End Date:</span> {{ formatDate(request.end_date) }}
                    </p>
                    <p class="text-sm text-gray-600">
                      <span class="font-medium">Requested:</span> {{ formatDateTime(request.quote_requested_at) }}
                    </p>
                  </div>
                </div>

                <div v-if="request.notes" class="mt-4">
                  <p class="text-sm font-medium text-gray-700">Special Requirements:</p>
                  <p class="text-sm text-gray-600 mt-1">{{ request.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Approval Form -->
            <div class="mt-6 pt-4 border-t border-gray-200">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Approve Form -->
                <form @submit.prevent="approveQuote(request)" class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Final Price ($)
                    </label>
                    <input
                      v-model.number="request.final_price"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      placeholder="299.00"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div class="flex items-end">
                    <button
                      type="submit"
                      :disabled="approving === request.id"
                      class="w-full btn-primary"
                    >
                      <span v-if="approving === request.id" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      {{ approving === request.id ? 'Approving...' : 'Approve Quote' }}
                    </button>
                  </div>
                </form>

                <!-- Decline Form -->
                <form @submit.prevent="declineQuote(request)" class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Decline Reason (Optional)
                    </label>
                    <textarea
                      v-model="request.decline_reason"
                      rows="2"
                      placeholder="e.g., Pricing not competitive, dates unavailable..."
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    ></textarea>
                  </div>
                  <div class="flex items-end">
                    <button
                      type="submit"
                      :disabled="declining === request.id"
                      class="w-full btn-secondary bg-red-600 hover:bg-red-700 text-white border-red-600"
                    >
                      <span v-if="declining === request.id" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      {{ declining === request.id ? 'Declining...' : 'Decline Quote' }}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State for Pending Requests -->
        <div v-else class="text-center py-12 bg-white rounded-lg shadow">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900">No pending quote requests</h3>
          <p class="mt-1 text-sm text-gray-500">All quote requests have been processed.</p>
        </div>
        </div>

        <!-- Approved Shows - Payment Management -->
        <div class="space-y-6">
          <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-lg font-medium text-gray-900">Approved Shows - Payment Management</h2>
            <p class="mt-1 text-sm text-gray-600">{{ approvedShows.length }} approved shows</p>
          </div>

          <!-- Approved Show Cards -->
          <div v-if="approvedShows.length > 0" class="space-y-4">
            <div
              v-for="show in approvedShows"
              :key="show.id"
              class="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 p-6"
            >
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <div class="flex items-center justify-between">
                    <h3 class="text-lg font-medium text-gray-900">{{ show.name }}</h3>
                    <div class="flex space-x-2">
                      <span class="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-medium bg-emerald-50 text-emerald-700">Approved</span>
                      <span :class="['inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-medium', show.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700']">
                        {{ show.payment_status === 'paid' ? 'Paid' : 'Payment Pending' }}
                      </span>
                    </div>
                  </div>
                  
                  <div class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p class="text-sm text-gray-600">
                        <span class="font-medium">Client:</span> {{ show.client_name }}
                      </p>
                      <p class="text-sm text-gray-600">
                        <span class="font-medium">Email:</span> {{ show.client_email }}
                      </p>
                      <p class="text-sm text-gray-600">
                        <span class="font-medium">Tablets:</span> {{ show.tablet_count }}
                      </p>
                    </div>
                    <div>
                      <p class="text-sm text-gray-600">
                        <span class="font-medium">Start Date:</span> {{ formatDate(show.start_date) }}
                      </p>
                      <p class="text-sm text-gray-600">
                        <span class="font-medium">End Date:</span> {{ formatDate(show.end_date) }}
                      </p>
                      <p class="text-sm text-gray-600">
                        <span class="font-medium">Final Price:</span> €{{ show.final_price }}
                      </p>
                    </div>
                  </div>

                  <div v-if="show.notes" class="mt-4">
                    <p class="text-sm font-medium text-gray-700">Notes:</p>
                    <p class="text-sm text-gray-600 mt-1">{{ show.notes }}</p>
                  </div>
                </div>
              </div>

              <!-- Payment Management -->
              <div v-if="show.payment_status !== 'paid'" class="mt-6 pt-4 border-t border-gray-200">
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm text-gray-600">
                      <span class="font-medium">Status:</span> Invoice sent, waiting for payment
                    </p>
                    <p class="text-sm text-gray-500 mt-1">
                      Click "Mark as Paid" once payment is received
                    </p>
                  </div>
                  <button
                    @click="markAsPaid(show)"
                    :disabled="markingPaid === show.id"
                    class="btn-primary"
                  >
                    <span v-if="markingPaid === show.id" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    {{ markingPaid === show.id ? 'Marking...' : 'Mark as Paid' }}
                  </button>
                </div>
              </div>

              <!-- Paid Status -->
              <div v-else class="mt-6 pt-4 border-t border-gray-200">
                <div class="flex items-center">
                  <svg class="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                  </svg>
                  <p class="text-sm text-green-700 font-medium">Payment received - Show is active</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State for Approved Shows -->
          <div v-else class="text-center py-12 bg-white rounded-lg shadow">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No approved shows</h3>
            <p class="mt-1 text-sm text-gray-500">Approved shows will appear here for payment management.</p>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { formatDate as fmtDate, formatDateTime as fmtDateTime } from '../utils/date.js'
import { useRouter } from 'vue-router'
import config from '../config.js'

// Router
const router = useRouter()

// Reactive data
const loading = ref(true)
const error = ref(null)
const quoteRequests = ref([])
const approvedShows = ref([])
const approving = ref(null)
const declining = ref(null)
const markingPaid = ref(null)

// Methods
const loadData = async () => {
  try {
    loading.value = true
    error.value = null
    
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    // Load pending quote requests
    const quoteResponse = await fetch(`${config.apiBaseUrl}/admin/super-admin/quote-requests`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!quoteResponse.ok) {
      if (quoteResponse.status === 401) {
        localStorage.removeItem('admin_token')
        router.push('/admin/login')
        return
      }
      if (quoteResponse.status === 403) {
        error.value = 'Super admin access required'
        return
      }
      throw new Error('Failed to load quote requests')
    }

    const quoteData = await quoteResponse.json()
    quoteRequests.value = quoteData.quote_requests.map(request => ({
      ...request,
      final_price: null,
      decline_reason: null
    }))

    // Load approved shows for payment management
    const approvedResponse = await fetch(`${config.apiBaseUrl}/admin/super-admin/approved-shows`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (approvedResponse.ok) {
      const approvedData = await approvedResponse.json()
      approvedShows.value = approvedData.shows
    }
    
  } catch (err) {
    console.error('Error loading data:', err)
    error.value = err.message || 'Failed to load data'
  } finally {
    loading.value = false
  }
}

const approveQuote = async (request) => {
  try {
    approving.value = request.id
    
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/super-admin/shows/${request.id}/approve`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        final_price: request.final_price
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to approve quote')
    }

    // Remove the approved request from the list
    quoteRequests.value = quoteRequests.value.filter(r => r.id !== request.id)
    
    // Reload data to show the approved show in the payment management section
    await loadData()
    
    // Show success message (you could add a toast notification here)
    alert('Quote approved successfully!')
    
  } catch (err) {
    console.error('Error approving quote:', err)
    alert(`Error approving quote: ${err.message}`)
  } finally {
    approving.value = null
  }
}

const declineQuote = async (request) => {
  try {
    declining.value = request.id
    
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/super-admin/shows/${request.id}/decline`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        reason: request.decline_reason || ''
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to decline quote')
    }

    // Remove the declined request from the list
    quoteRequests.value = quoteRequests.value.filter(r => r.id !== request.id)
    
    // Show success message
    alert('Quote declined successfully!')
    
  } catch (err) {
    console.error('Error declining quote:', err)
    alert(`Error declining quote: ${err.message}`)
  } finally {
    declining.value = null
  }
}

const markAsPaid = async (show) => {
  try {
    markingPaid.value = show.id
    
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/super-admin/shows/${show.id}/mark-paid`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to mark as paid')
    }

    // Update the show's payment status locally
    const showIndex = approvedShows.value.findIndex(s => s.id === show.id)
    if (showIndex !== -1) {
      approvedShows.value[showIndex].payment_status = 'paid'
    }
    
    // Show success message
    alert('Payment marked as received! Show is now active.')
    
  } catch (err) {
    console.error('Error marking as paid:', err)
    alert(`Error marking as paid: ${err.message}`)
  } finally {
    markingPaid.value = null
  }
}

const formatDate = (dateString) => fmtDate(dateString)
const formatDateTime = (dateString) => fmtDateTime(dateString)

const navigateTo = (path) => {
  router.push(path)
}

// Lifecycle
onMounted(() => {
  loadData()
})
</script> 